import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AdminChannelsModal } from "../components/AdminChannelsModal";
import { AiConfigModal } from "../components/AiConfigModal";
import { CoachingSessionsModal } from "../components/CoachingSessionsModal";
import { KebabMenu, type KebabMenuItem } from "../components/KebabMenu";
import { McpGatewayModal } from "../components/McpGatewayModal";
import { ModalShell } from "../components/ModalShell";
import { ReassignModal } from "../components/ReassignModal";
import { ResizeInstanceModal } from "../components/ResizeInstanceModal";
import { ResizeScheduleModal } from "../components/ResizeScheduleModal";
import { DeleteModal } from "../components/DeleteModal";
import { StopConfirmModal } from "../components/StopConfirmModal";
import { OnboardingSubmissionDialog } from "../components/OnboardingSubmissionDialog";
import { Toast, toneForMessage, useToast } from "../components/Toast";
import { AdminTableSkeleton } from "../components/admin/AdminTableSkeleton";
import { useAdminClaws } from "../hooks/useAdminClaws";
import { useAdminStatus, type TenantHealthTimeline } from "../hooks/useAdminStatus";
import { useAuth } from "../hooks/useAuth";
import { useImpersonation } from "../hooks/useImpersonation";
import { HealthStrip } from "../components/admin/HealthStrip";
import { prefetchInstanceDetail } from "../lib/adminPrefetch";
import type { AdminClawDisk, AdminOpenClaw, OpenClaw, OpenClawStatus, TenantHealth } from "../types/openclaw";
import { formatDateTime, formatRelativeTime } from "../utils/formatDateTime";
import flagStyles from "./admin/AdminFeatureFlags.module.css";
import styles from "./AdminInstances.module.css";
import { AdminPageIntro } from "../components/admin/AdminPageIntro";

const statusLabel = (status: OpenClawStatus): string => {
  switch (status) {
    case "RUNNING": return "Running";
    case "PROVISIONING": return "Provisioning";
    case "FAILED": return "Provisioning Failed";
    case "RESIZING": return "Resizing";
  }
};

// Mid-transition statuses where VM-touching admin actions should be disabled
// (the backend guards these too, but the UI should grey them out + explain).
const isBusyStatus = (status: OpenClawStatus): boolean =>
  status === "PROVISIONING" || status === "RESIZING";

const busyReason = (status: OpenClawStatus): string =>
  status === "RESIZING" ? "Wait for the resize to finish." : "Wait for provisioning to finish.";

const formatTime = (iso: string): string => {
  if (!iso) return "—";
  const formatted = formatDateTime(iso);
  return formatted === "" ? "—" : formatted;
};

const dashboardUrl = (claw: AdminOpenClaw): string =>
  `${window.location.origin}/claw-launch/${encodeURIComponent(claw.tenantId)}`;

const healthLabel = (state: TenantHealth["state"]): string => {
  switch (state) {
    case "HEALTHY": return "Healthy";
    case "DEGRADED": return "Degraded";
    case "DOWN": return "Down";
    case "UNREACHABLE": return "Unreachable";
    // A newer backend may ship states this bundle doesn't know yet.
    default: return String(state);
  }
};

// Matches docs/runbooks/access-tenant-instances.md — the operator shell
// command for a tenant EC2. Only meaningful for real EC2 ids (prod);
// local-dev instance ids are "host:port" Lima endpoints.
const ssmCommand = (instanceId: string): string =>
  `AWS_PROFILE=ztc-claws-prod aws ssm start-session --target ${instanceId}`;

const isEc2InstanceId = (instanceId: string | null | undefined): instanceId is string =>
  instanceId?.startsWith("i-") ?? false;

// AdminOpenClaw extends OpenClaw, so its shape satisfies the modals'
// OpenClaw prop — narrow with a cast at the call site rather than copying
// fields.
const toOpenClaw = (c: AdminOpenClaw | null): OpenClaw | null => c;

const diskPct = (disk: AdminClawDisk): number =>
  disk.totalGb > 0 ? Math.round((disk.usedGb / disk.totalGb) * 100) : 0;

const diskLevel = (pct: number): "ok" | "warn" | "crit" =>
  pct >= 90 ? "crit" : pct >= 80 ? "warn" : "ok";

// Problems-first health ordering within each section: failures float to
// the top, then in-flight provisioning, then RUNNING fullest-disk-first.
// RESIZING is an operational box mid-transition, not a problem — rank it with
// RUNNING so it sorts among healthy instances, not up with failures.
const STATUS_RANK: Record<OpenClawStatus, number> = { FAILED: 0, PROVISIONING: 1, RUNNING: 2, RESIZING: 2 };

const compareClaws = (a: AdminOpenClaw, b: AdminOpenClaw): number => {
  if (STATUS_RANK[a.status] !== STATUS_RANK[b.status]) return STATUS_RANK[a.status] - STATUS_RANK[b.status];
  const aPct = a.disk ? diskPct(a.disk) : -1;
  const bPct = b.disk ? diskPct(b.disk) : -1;
  if (aPct !== bPct) return bPct - aPct;
  const aCreated = a.createdAt ? Date.parse(a.createdAt) : 0;
  const bCreated = b.createdAt ? Date.parse(b.createdAt) : 0;
  if (aCreated !== bCreated) return bCreated - aCreated;
  return a.name.localeCompare(b.name);
};

const DiskCell = ({ disk }: { disk: AdminClawDisk | null | undefined }): JSX.Element => {
  if (!disk) return <span className={styles.muted}>&mdash;</span>;
  const pct = diskPct(disk);
  const level = diskLevel(pct);
  const fillClass = level === "ok" ? styles.diskFill : `${styles.diskFill} ${styles[`diskFill_${level}`]}`;
  const pctClass = level === "ok" ? styles.diskPct : `${styles.diskPct} ${styles[`diskPct_${level}`]}`;
  return (
    <span
      className={styles.diskCell}
      title={`${disk.usedGb.toFixed(1)} / ${disk.totalGb.toFixed(0)} GB · read ${formatRelativeTime(disk.readAt)}`}
    >
      <span className={styles.diskGauge}>
        <span className={styles.diskBar}>
          <span className={fillClass} style={{ width: `${Math.min(pct, 100)}%` }} />
        </span>
        <span className={pctClass}>{pct}%</span>
      </span>
      <span className={styles.diskText}>
        {disk.usedGb.toFixed(1)} / {disk.totalGb.toFixed(0)} GB
      </span>
    </span>
  );
};

const formatGib = (gib: number): string => (Number.isInteger(gib) ? `${gib}` : gib.toFixed(1));

// EC2 instance type + RAM, so the table doubles as a who-runs-on-what
// overview for the resize lever. Unknown until the backend capacity sweep
// (every 5 min, and right after a resize) has seen the box.
const InstanceCell = ({ claw }: { claw: AdminOpenClaw }): JSX.Element => {
  if (!claw.instanceType) {
    return (
      <span className={styles.muted} title="Instance type not read yet (refreshed every 5 min)">
        &mdash;
      </span>
    );
  }
  return (
    <span className={styles.instanceCell} title="EC2 instance type · refreshed every 5 min and after a resize">
      <span className={styles.instanceType}>{claw.instanceType}</span>
      {typeof claw.instanceMemGib === "number" && (
        <span className={styles.instanceSub}>{formatGib(claw.instanceMemGib)} GB RAM</span>
      )}
    </span>
  );
};

type InstanceAccessModalProps = {
  claw: AdminOpenClaw;
  onClose: () => void;
};

// Read-only roster of everyone who can use this instance: the owner plus
// any instance_members collaborators. Data rides on the admin list
// payload — no fetch. Managing membership stays a user-side (owner)
// concern; admins impersonate if they need to change it.
const InstanceAccessModal = ({ claw, onClose }: InstanceAccessModalProps): JSX.Element => {
  const titleId = `instance-access-title-${claw.tenantId}`;
  const members = claw.members ?? [];
  return (
    <ModalShell density="compact" onClose={onClose} titleId={titleId} size="sm" width="min(640px, 92vw)">
      <header className={flagStyles.dialogHeader}>
        <h3 className={flagStyles.dialogTitle} id={titleId}>
          Access — <code>{claw.name}</code>
        </h3>
        <button type="button" className={flagStyles.closeBtn} onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>

      <ul className={styles.accessList}>
        <li className={styles.accessRow}>
          <span className={styles.accessEmail}>{claw.ownerEmail}</span>
          <span className={styles.pill}>Owner</span>
        </li>
        {members.map((member) => (
          <li key={member.email} className={styles.accessRow}>
            <span className={styles.accessEmail}>{member.email}</span>
            <span className={styles.pill}>{member.role === "MANAGER" ? "Manager" : "Member"}</span>
          </li>
        ))}
      </ul>
      {members.length === 0 && (
        <p className={flagStyles.hint}>Not shared with anyone — only the owner has access.</p>
      )}
    </ModalShell>
  );
};

export const AdminInstances = (): JSX.Element => {
  const {
    status, claws, error, refresh,
    reassign, deleteClaw,
    unlock, lock, stop, resume, setCoachingSessions, resizeInstance,
    getResizeSchedule, saveResizeSchedule, getResizeConfig, getOnboardingSubmission,
    refreshHealth,
    getAiStatus, applyAiConfig,
    getMcpTokenStatus, rotateMcpToken, pushMcpToken,
    triggerDiskSweep,
  } = useAdminClaws();
  const navigate = useNavigate();
  const [reassigning, setReassigning] = useState<AdminOpenClaw | null>(null);
  const [deleting, setDeleting] = useState<AdminOpenClaw | null>(null);
  const [aiConfigClaw, setAiConfigClaw] = useState<AdminOpenClaw | null>(null);
  const [channelsClaw, setChannelsClaw] = useState<AdminOpenClaw | null>(null);
  const [viewingSubmission, setViewingSubmission] = useState<AdminOpenClaw | null>(null);
  const [coachingClaw, setCoachingClaw] = useState<AdminOpenClaw | null>(null);
  const [stopping, setStopping] = useState<AdminOpenClaw | null>(null);
  const [mcpClaw, setMcpClaw] = useState<AdminOpenClaw | null>(null);
  const [accessClaw, setAccessClaw] = useState<AdminOpenClaw | null>(null);
  const [resizingClaw, setResizingClaw] = useState<AdminOpenClaw | null>(null);
  const [scheduleClaw, setScheduleClaw] = useState<AdminOpenClaw | null>(null);
  const { toast, showToast, dismissToast } = useToast();
  const [probingTenantId, setProbingTenantId] = useState<string | null>(null);
  const { start: startImpersonation } = useImpersonation();
  const { user: currentUser } = useAuth();
  const [impersonatingTenantId, setImpersonatingTenantId] = useState<string | null>(null);

  // Same signature the modals' onFlash plumbing expects; renders as a
  // fixed toast instead of an inline banner so the table never shifts.
  const setFlash = useCallback((message: string) => {
    showToast(message, toneForMessage(message));
  }, [showToast]);

  // Locked instances (new signups awaiting setup, or admin access cuts)
  // need admin attention, so they get their own section on top.
  const { locked, customers, internal } = useMemo(() => {
    const sorted = [...claws].sort(compareClaws);
    // Stopped (paused) instances aren't customer-reachable, so they live with
    // the locked rows rather than mixed into the live Customers/Internal lists.
    return {
      locked: sorted.filter((c) => !c.unlocked || c.stopped),
      customers: sorted.filter((c) => c.unlocked && !c.stopped && !(c.internal ?? false)),
      internal: sorted.filter((c) => c.unlocked && !c.stopped && (c.internal ?? false)),
    };
  }, [claws]);

  // Recent health-history strips, reusing the cached fleet-history query
  // the Status tab already fetches (24h window). Joined by tenantId; the
  // Status column slices to the most recent buckets.
  const { history: healthHistory } = useAdminStatus(24);
  const timelineByTenant = useMemo(() => {
    const m = new Map<string, TenantHealthTimeline>();
    for (const t of healthHistory?.tenants ?? []) m.set(t.tenantId, t);
    return m;
  }, [healthHistory]);
  const historyBucketSeconds = healthHistory?.bucketSeconds ?? 1800;

  const summary = useMemo(() => {
    const parts: string[] = [`${claws.length} instance${claws.length === 1 ? "" : "s"}`];
    const lockedCount = claws.filter((c) => !c.unlocked).length;
    if (lockedCount > 0) parts.push(`${lockedCount} locked`);
    const failed = claws.filter((c) => c.status === "FAILED").length;
    if (failed > 0) parts.push(`${failed} failed`);
    // Fleet health at a glance, replacing what the Status tab used to show.
    const unhealthy = claws.filter(
      (c) => c.status === "RUNNING" && !c.stopped && c.health != null && c.health.state !== "HEALTHY",
    ).length;
    if (unhealthy > 0) parts.push(`${unhealthy} unhealthy`);
    const fullDisks = claws.filter((c) => c.disk && diskPct(c.disk) >= 80).length;
    if (fullDisks > 0) parts.push(`${fullDisks} disk${fullDisks === 1 ? "" : "s"} ≥80%`);
    return parts.join(" · ");
  }, [claws]);

  const handleUnlock = async (claw: AdminOpenClaw): Promise<void> => {
    const result = await unlock(claw.tenantId);
    if (result.ok) {
      setFlash(`Unlocked ${result.claw.name}.`);
    } else {
      setFlash(`Unlock failed: ${result.message}`);
    }
  };

  // Same session switch as Users > "View as user"; lands on this instance's
  // tab so an owner with several instances opens on the one clicked.
  const isOwnInstance = (claw: AdminOpenClaw): boolean =>
    claw.ownerEmail.toLowerCase() === currentUser?.email.toLowerCase();

  const handleViewAsOwner = async (claw: AdminOpenClaw): Promise<void> => {
    setImpersonatingTenantId(claw.tenantId);
    dismissToast();
    const result = await startImpersonation(claw.ownerEmail);
    if (result.ok) {
      window.location.href = `/dashboard/overview?instance=${encodeURIComponent(claw.tenantId)}`;
      return;
    }
    setFlash(`Impersonation failed: ${result.message}`);
    setImpersonatingTenantId(null);
  };

  const copyText = async (text: string, message: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setFlash(message);
    } catch {
      setFlash("Copy failed — clipboard unavailable in this browser context.");
    }
  };

  const handleDiskSweep = async (): Promise<void> => {
    const result = await triggerDiskSweep();
    if (!result.ok) {
      setFlash("Disk sweep failed to start.");
      return;
    }
    setFlash(result.started
      ? "Disk sweep started — readings update as they land."
      : "A disk sweep is already running — readings update shortly.");
    // No completion signal from the async sweep; refetch on a short
    // schedule so fresh readings appear without a manual reload.
    window.setTimeout(() => { void refresh(); }, 5_000);
    window.setTimeout(() => { void refresh(); }, 20_000);
  };

  const handleRefreshHealth = async (claw: AdminOpenClaw): Promise<void> => {
    if (probingTenantId !== null) return;
    setProbingTenantId(claw.tenantId);
    setFlash(`Probing ${claw.name}…`);
    try {
      const health = await refreshHealth(claw.tenantId);
      setFlash(health
        ? `${claw.name}: ${healthLabel(health.state)}${health.reasons.length > 0 ? ` (${health.reasons.join(", ")})` : ""}`
        : `Health probe failed for ${claw.name}.`);
    } finally {
      setProbingTenantId(null);
    }
  };

  const handleLock = async (claw: AdminOpenClaw): Promise<void> => {
    const result = await lock(claw.tenantId);
    if (result.ok) {
      setFlash(`Locked ${result.claw.name} — hidden from ${result.claw.ownerEmail} until unlocked.`);
    } else {
      setFlash(`Lock failed: ${result.message}`);
    }
  };

  const handleResume = async (claw: AdminOpenClaw): Promise<void> => {
    const result = await resume(claw.tenantId);
    if (result.ok) {
      setFlash(`Resumed ${result.claw.name}.`);
    } else {
      setFlash(`Resume failed: ${result.message}`);
    }
  };

  // The chevron next to the copy button: everything else copyable for
  // this row. The plain copy button handles the instance id itself.
  const copyMenuItems = (claw: AdminOpenClaw): KebabMenuItem[] => [
    ...(isEc2InstanceId(claw.instanceId)
      ? [
          {
            label: "SSM command",
            onClick: () => {
              void copyText(ssmCommand(claw.instanceId as string), "Copied SSM command.");
            },
          },
          {
            label: "EC2 id",
            onClick: () => {
              void copyText(claw.instanceId as string, "Copied EC2 id.");
            },
          },
        ]
      : []),
    ...(claw.status === "RUNNING"
      ? [{
          label: "Dashboard URL",
          onClick: () => {
            void copyText(dashboardUrl(claw), "Copied dashboard URL.");
          },
        }]
      : []),
  ];

  // Collapsed Status/Health column: provisioning pill until RUNNING, then a
  // compact recent-history strip (rightmost bar = now; hover a bar for state
  // + reasons). Em dash until the first probe lands.
  const renderStatus = (claw: AdminOpenClaw): JSX.Element => {
    // Paused (EC2 stopped) takes precedence over the health strip — the VM
    // is off, so a "Stopped" pill is the honest signal regardless of status.
    if (claw.stopped) {
      return <span className={`${styles.pill} ${styles.pill_STOPPED}`}>Stopped</span>;
    }
    if (claw.status !== "RUNNING") {
      return (
        <span className={`${styles.pill} ${styles[`pill_${claw.status}`] ?? ""}`}>
          {statusLabel(claw.status)}
        </span>
      );
    }
    const timeline = timelineByTenant.get(claw.tenantId);
    const hasBars = timeline?.buckets.some((b) => b.state != null) ?? false;
    if (timeline && hasBars) {
      return (
        <div className={styles.healthCell}>
          <HealthStrip
            buckets={timeline.buckets}
            bucketSeconds={historyBucketSeconds}
            compact
            maxBuckets={16}
          />
        </div>
      );
    }
    return (
      <span
        className={styles.muted}
        title="No health probe yet — auto-checks run every few minutes; use Refresh health in the row menu to probe now."
      >
        &mdash;
      </span>
    );
  };

  const renderRow = (claw: AdminOpenClaw): JSX.Element => (
    <tr key={claw.tenantId}>
      <td>
        <span className={styles.idLine}>
          <Link
            to={claw.tenantId}
            className={`${styles.instanceId} ${styles.instanceLink}`}
            title={`${claw.tenantId} · created ${formatTime(claw.createdAt)}`}
            onMouseEnter={() => prefetchInstanceDetail(claw.tenantId)}
            onFocus={() => prefetchInstanceDetail(claw.tenantId)}
          >
            {claw.name}
          </Link>
          <span className={styles.tierTag}>{claw.tier}</span>
          <button
            type="button"
            className={styles.copyBtn}
            onClick={() => void copyText(claw.tenantId, "Copied instance id.")}
            aria-label={`Copy instance id for ${claw.name}`}
            title="Copy instance id"
          >
            <svg viewBox="0 0 24 24" className={styles.copyIcon} aria-hidden>
              <rect x="9" y="9" width="13" height="13" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" fill="none" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
          {copyMenuItems(claw).length > 0 && (
            <KebabMenu
              trigger="chevron"
              ariaLabel={`More copy options for ${claw.name}`}
              items={copyMenuItems(claw)}
            />
          )}
        </span>
        <span className={styles.ownerLine}>
          <span className={styles.ownerEmail} title={claw.ownerEmail}>
            {claw.ownerEmail}
          </span>
          {(claw.members?.length ?? 0) > 0 && (
            <button
              type="button"
              className={styles.accessChip}
              onClick={() => setAccessClaw(claw)}
              aria-label={`View access for ${claw.name}`}
              title={`Shared with ${claw.members?.length} other user${claw.members?.length === 1 ? "" : "s"}`}
            >
              +{claw.members?.length}
            </button>
          )}
        </span>
      </td>
      <td>{renderStatus(claw)}</td>
      <td className={styles.muted}>
        {claw.health?.runningVersion ? (
          <>
            <span
              className={
                !claw.health.fleetVersion
                  ? undefined
                  : claw.health.versionOutdated
                    ? styles.version_behind
                    : styles.version_current
              }
              title={
                !claw.health.fleetVersion
                  ? undefined
                  : claw.health.versionOutdated
                    ? `Differs from the fleet pin (${claw.health.fleetVersion}).`
                    : "Up to date with the fleet pin."
              }
            >
              {claw.health.runningVersion}
            </span>
            {claw.health.restartPending && (
              <span
                className={styles.restartDot}
                title={`Installed ${claw.health.installedVersion ?? "?"} but running ${claw.health.runningVersion} — restart pending.`}
              />
            )}
          </>
        ) : (
          <span>&mdash;</span>
        )}
      </td>
      <td>
        <InstanceCell claw={claw} />
      </td>
      <td>
        <DiskCell disk={claw.disk} />
      </td>
      <td className={styles.actionCell}>
        <div className={styles.actionCellInner}>
          {impersonatingTenantId === claw.tenantId && (
            <span
              className={`${styles.actionStatus} ${styles.actionSpinner}`}
              role="status"
              aria-label={`Opening ${claw.name} as its owner`}
            />
          )}
          <KebabMenu
            ariaLabel={`Actions for ${claw.name}`}
            items={[
              // Unlock / Resume lead the menu on the rows that need them (the
              // Locked / paused section), mirroring Lock and Stop further down,
              // so the actions column is only the kebab on every row. A stopped
              // row offers Resume instead - Unlock has no effect while the VM is off.
              ...(claw.stopped
                ? [{ label: "Resume", positive: true, onClick: () => { void handleResume(claw); } }]
                : !claw.unlocked
                  ? [{ label: "Unlock", positive: true, onClick: () => { void handleUnlock(claw); } }]
                  : []),
              {
                label: "View as owner",
                onClick: () => { void handleViewAsOwner(claw); },
                disabled: impersonatingTenantId !== null || isOwnInstance(claw),
                ...(isOwnInstance(claw) ? { disabledReason: "You can't impersonate yourself" } : {}),
              },
              {
                label: "Who has access",
                onClick: () => setAccessClaw(claw),
              },
              {
                label: "Submission",
                onClick: () => setViewingSubmission(claw),
              },
              {
                label: probingTenantId === claw.tenantId ? "Probing…" : "Refresh health",
                onClick: () => { void handleRefreshHealth(claw); },
                disabled: isBusyStatus(claw.status) || claw.stopped || probingTenantId !== null,
                disabledReason: probingTenantId !== null
                  ? "A health probe is already running."
                  : claw.stopped
                    ? "Resume the instance first."
                    : busyReason(claw.status),
              },
              {
                label: "AI config",
                onClick: () => setAiConfigClaw(claw),
                disabled: isBusyStatus(claw.status) || claw.stopped,
                disabledReason: claw.stopped
                  ? "Resume the instance first."
                  : busyReason(claw.status),
              },
              {
                label: "MCP Gateway",
                onClick: () => setMcpClaw(claw),
                disabled: isBusyStatus(claw.status),
                disabledReason: busyReason(claw.status),
              },
              {
                label: "Channels",
                onClick: () => setChannelsClaw(claw),
              },
              {
                label: "Coaching sessions",
                onClick: () => setCoachingClaw(claw),
              },
              {
                label: "Reassign",
                onClick: () => setReassigning(claw),
                disabled: isBusyStatus(claw.status),
                disabledReason: busyReason(claw.status),
              },
              ...(claw.resizeEnabled
                ? [{
                    label: "Resize instance",
                    onClick: () => setResizingClaw(claw),
                    // Manual resize needs a steady RUNNING box; a resize in
                    // flight (RESIZING) would 409.
                    disabled: isBusyStatus(claw.status) || claw.stopped,
                    disabledReason: claw.stopped ? "Resume the instance first." : busyReason(claw.status),
                  }, {
                    label: "Resize schedule",
                    onClick: () => setScheduleClaw(claw),
                  }]
                : []),
              ...(claw.unlocked
                ? [{
                    label: "Lock",
                    destructive: true,
                    onClick: () => { void handleLock(claw); },
                  }]
                : []),
              ...(claw.status === "RUNNING" && !claw.stopped
                ? [{
                    label: "Stop",
                    onClick: () => setStopping(claw),
                  }]
                : []),
              {
                label: "Delete",
                destructive: true,
                onClick: () => setDeleting(claw),
              },
            ]}
          />
        </div>
      </td>
    </tr>
  );

  const renderSection = (label: string, rows: ReadonlyArray<AdminOpenClaw>): JSX.Element => (
    <tbody>
      <tr className={styles.sectionRow}>
        <td colSpan={6}>
          {label} ({rows.length})
        </td>
      </tr>
      {rows.map(renderRow)}
    </tbody>
  );

  return (
    <div className={styles.page}>
      <AdminPageIntro title="Instances">
        Every OpenClaw, locked first. Hover a name for its id and created date; the arrow copies its SSM command, EC2 id or dashboard URL.
      </AdminPageIntro>

      <section className={styles.toolbar}>
        <span className={styles.toolbarMeta}>
          {status === "loading" && "Loading…"}
          {status === "ready" && summary}
          {status === "error" && (error ?? "Failed to load.")}
        </span>
        <div className={styles.toolbarActions}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => { void handleDiskSweep(); }}
            title="Re-read disk usage across all running instances now."
          >
            Sweep disks
          </button>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => navigate("/dashboard/onboarding")}
          >
            Launch new instance
          </button>
        </div>
      </section>

      <section className={styles.listSection}>
        {status === "loading" && (
          <AdminTableSkeleton
            headers={["Instance", "Status", "Version", "Type", "Disk", ""]}
            tableClassName={styles.table ?? ""}
          />
        )}

        {status === "ready" && claws.length === 0 && (
          <div className={styles.empty}>
            No instances yet. Click <strong>Launch new instance</strong> to create one.
          </div>
        )}

        {status === "ready" && claws.length > 0 && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Instance</th>
                <th>Status</th>
                <th>Version</th>
                <th>Type</th>
                <th>Disk</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            {locked.length > 0 && renderSection("Locked / paused", locked)}
            {locked.length > 0 || internal.length > 0
              ? renderSection("Customers", customers)
              : <tbody>{customers.map(renderRow)}</tbody>}
            {internal.length > 0 && renderSection("Internal", internal)}
          </table>
        )}
      </section>

      <Toast toast={toast} onDismiss={dismissToast} />

      {accessClaw && (
        <InstanceAccessModal claw={accessClaw} onClose={() => setAccessClaw(null)} />
      )}

      <ReassignModal
        open={reassigning !== null}
        claw={toOpenClaw(reassigning)}
        onClose={() => setReassigning(null)}
        onSubmit={async (tenantId, email) => {
          const result = await reassign(tenantId, email);
          if (result.ok) setFlash(`Reassigned ${result.claw.name} to ${email}.`);
          return result;
        }}
      />

      <DeleteModal
        open={deleting !== null}
        claw={toOpenClaw(deleting)}
        onClose={() => setDeleting(null)}
        onSubmit={async (tenantId) => {
          const result = await deleteClaw(tenantId);
          if (result.ok) setFlash(`Deleted ${deleting?.name ?? "instance"}.`);
          return result;
        }}
      />

      <StopConfirmModal
        open={stopping !== null}
        claw={toOpenClaw(stopping)}
        onClose={() => setStopping(null)}
        onConfirm={async (tenantId) => {
          const result = await stop(tenantId);
          if (result.ok) setFlash(`Stopped ${stopping?.name ?? "instance"}.`);
          return result;
        }}
      />

      <McpGatewayModal
        open={mcpClaw !== null}
        claw={mcpClaw}
        fetchStatus={getMcpTokenStatus}
        onRotate={rotateMcpToken}
        onPush={pushMcpToken}
        onFlash={setFlash}
        onClose={() => setMcpClaw(null)}
      />

      <AiConfigModal
        open={aiConfigClaw !== null}
        claw={aiConfigClaw}
        fetchAiStatus={getAiStatus}
        onApplyAiConfig={applyAiConfig}
        onClose={() => setAiConfigClaw(null)}
        onFlash={setFlash}
      />

      <CoachingSessionsModal
        open={coachingClaw !== null}
        claw={coachingClaw}
        onClose={() => setCoachingClaw(null)}
        onSubmit={async (tenantId, remaining) => {
          const result = await setCoachingSessions(tenantId, remaining);
          if (result.ok) setFlash(`Set ${result.claw.name} to ${remaining} coaching session${remaining === 1 ? "" : "s"}.`);
          return result;
        }}
      />

      <ResizeInstanceModal
        open={resizingClaw !== null}
        claw={resizingClaw}
        onClose={() => setResizingClaw(null)}
        fetchConfig={getResizeConfig}
        onSubmit={async (tenantId, instanceType) => {
          const result = await resizeInstance(tenantId, instanceType);
          if (result.ok) setFlash(`Resizing ${resizingClaw?.name ?? "instance"} to ${instanceType} — brief interruption expected.`);
          return result;
        }}
      />

      <ResizeScheduleModal
        open={scheduleClaw !== null}
        claw={scheduleClaw}
        onClose={() => setScheduleClaw(null)}
        fetchConfig={getResizeConfig}
        fetchSchedule={getResizeSchedule}
        onSubmit={async (tenantId, body) => {
          const result = await saveResizeSchedule(tenantId, body);
          if (result.ok) setFlash(`Saved resize schedule for ${scheduleClaw?.name ?? "instance"}.`);
          return result;
        }}
      />

      <OnboardingSubmissionDialog
        open={viewingSubmission !== null}
        tenantId={viewingSubmission?.tenantId ?? null}
        tenantName={viewingSubmission?.name ?? null}
        fetch={getOnboardingSubmission}
        onClose={() => setViewingSubmission(null)}
      />

      <AdminChannelsModal
        open={channelsClaw !== null}
        claw={channelsClaw}
        onClose={() => setChannelsClaw(null)}
        onFlash={setFlash}
      />
    </div>
  );
};
